/**
 * Toilet Finder — state machine + search + renderers.
 *
 * Philosophy: the search path and the quiz path produce the same answer shape
 * ({ type, screw, tank, valve }), which gets funneled into a single resolver.
 * Write the result logic once.
 */
(function () {
  'use strict';

  // ---------- Microsoft Clarity tracking -----------------------------------
  // Fires custom events into Clarity's funnel so Jonathan can track quiz
  // engagement. Safe-guarded against the script not being loaded — we
  // never throw if window.clarity is missing.
  //
  // Events:
  //   "Toilet Compatibility Quiz - Start Search"
  //     Fires once per page load when the user picks a model from search.
  //     Tagged with the model name for distribution analysis.
  //   "Toilet Compatibility Quiz - Start IDK"
  //     Fires once per page load when the user clicks "I don't know my
  //     toilet" to start the quiz.
  //   "Toilet Compatibility Quiz - End Results"
  //     Fires once per quiz session when the user reaches a result page.
  //     Tagged with the design they landed on (design_1 … design_8). If
  //     the user re-resolves to a different design via Change, the tag
  //     updates to the new design but the event count does not increment
  //     — one funnel completion per quiz attempt. The flag is reset when
  //     a new quiz starts (selectModel or startQuiz) so a fresh quiz
  //     emits the event again.
  let hasFiredStartSearch = false;
  let hasFiredStartIDK = false;
  let hasFiredEndResults = false;
  function track(eventName, data) {
    try {
      if (typeof window.clarity !== 'function') return;
      window.clarity('event', eventName);
      // Optional: attach metadata as custom tags so you can filter sessions
      // in Clarity's dashboard by model name or final design.
      if (data && typeof data === 'object') {
        for (const key in data) {
          if (Object.prototype.hasOwnProperty.call(data, key)) {
            window.clarity('set', key, String(data[key]));
          }
        }
      }
    } catch (_) { /* swallow — analytics failures must never break UX */ }
  }
  function trackStartSearch(modelName) {
    if (hasFiredStartSearch) return;
    hasFiredStartSearch = true;
    track('Toilet Compatibility Quiz - Start Search', { tf_model_name: modelName });
    // Starting a new quiz resets the completion flag so the next result
    // landing fires the End Results event again.
    hasFiredEndResults = false;
  }
  function trackStartIDK() {
    if (hasFiredStartIDK) return;
    hasFiredStartIDK = true;
    track('Toilet Compatibility Quiz - Start IDK');
    hasFiredEndResults = false;
  }
  function trackEndResults(design) {
    // Always update the design tag — if the user re-resolves to a
    // different design via Change, Clarity should reflect their most
    // recent (likely final) result. Done outside the dedup so the tag
    // stays accurate even when the event count stays at 1.
    try {
      if (typeof window.clarity === 'function') {
        window.clarity('set', 'tf_result_design', String(design));
      }
    } catch (_) {}
    // Fire the event itself only once per quiz session — re-resolves
    // shouldn't inflate the funnel-completion count.
    if (hasFiredEndResults) return;
    hasFiredEndResults = true;
    track('Toilet Compatibility Quiz - End Results');
  }

  // ---------- Flow definition ----------------------------------------------
  // Each state declares which answer field it captures and where to go next.
  // `next` can be a string (always) or a function (branching). Note that
  // `q4_hose` and `q4_valve` are on mutually exclusive branches — hose asks
  // about supply rigidity when the user DOES have tank access, valve asks
  // about alternate water sources when they DON'T.
  const FLOW = {
    q1_type: {
      field: 'type',
      question: 'What type of toilet do you have?',
      help: 'How to tell?',
      kind: 'cards',
      options: [
        { value: 'two_piece', label: 'Two-Piece', image: 'https://cdn.shopify.com/s/files/1/0563/2848/7080/files/toiletcomapatibilityquiz_-_two_piece.jpg?v=1777420297' },
        { value: 'one_piece', label: 'One-Piece', image: 'https://cdn.shopify.com/s/files/1/0563/2848/7080/files/toiletcomapatibilityquiz_-_one_piece.jpg?v=1777420297' },
        { value: 'other',     label: 'Other',     image: 'https://cdn.shopify.com/s/files/1/0563/2848/7080/files/toiletcomapatibilityquiz_-_other.jpg?v=1777420297' }
      ],
      // Two-piece toilets implicitly have >1" screw-to-tank distance so they
      // skip q2_screw. The screw field is pre-filled to 'more' in answer()
      // when the user picks two_piece.
      next: v => {
        if (v === 'other') return 'result';
        if (v === 'two_piece') return 'q3_tank';
        return 'q2_screw';
      }
    },
    q2_screw: {
      field: 'screw',
      question: 'What is the screw to tank distance?',
      help: 'How to tell?',
      kind: 'binary',
      // Hint image rendered above the choice buttons. The renderer falls
      // back to '#' if hintImage is missing, so swapping URLs is just a
      // matter of editing this string in place.
      hintImage: 'https://cdn.shopify.com/s/files/1/0563/2848/7080/files/toiletcomapatibilityquiz_-_screw_to_tank_distance.jpg?v=1777420157',
      options: [
        { value: 'more', label: 'More than 1"' },
        { value: 'less', label: 'Less than 1"' }
      ],
      next: () => 'q3_tank'
    },
    q3_tank: {
      field: 'tank',
      question: 'Do you have access to your tank connection?',
      kind: 'cards',
      options: [
        { value: 'yes', label: 'Yes', image: 'https://cdn.shopify.com/s/files/1/0563/2848/7080/files/toiletcomapatibilityquiz_-_yes_tank_connection.jpg?v=1777420544' },
        { value: 'no',  label: 'No',  image: 'https://cdn.shopify.com/s/files/1/0563/2848/7080/files/toiletcomapatibilityquiz_-_no_tank_connection.jpg?v=1777420544' }
      ],
      // tank=yes → ask about hose rigidity (we know they can connect).
      // tank=no  → ask about alternate water valves.
      next: v => v === 'yes' ? 'q4_hose' : 'q4_valve'
    },
    q4_hose: {
      field: 'hose',
      question: 'What type of hose connection do you have?',
      kind: 'cards',
      options: [
        { value: 'rigid',    label: 'Rigid',    image: 'https://cdn.shopify.com/s/files/1/0563/2848/7080/files/toiletcomapatibilityquiz_-_rigid.jpg?v=1777420157' },
        { value: 'flexible', label: 'Flexible', image: 'https://cdn.shopify.com/s/files/1/0563/2848/7080/files/toiletcomapatibilityquiz_-_flexible.jpg?v=1777420157' }
      ],
      next: () => 'result'
    },
    q4_valve: {
      field: 'valve',
      question: 'Can you access any water valve in your bathroom?',
      kind: 'cards',
      options: [
        { value: 'wall', label: 'Yes, on the wall behind my toilet', image: 'https://cdn.shopify.com/s/files/1/0563/2848/7080/files/image_-_behind_toilet_wall.jpg?v=1777420157' },
        { value: 'sink', label: 'Yes, under a nearby sink',          image: 'https://cdn.shopify.com/s/files/1/0563/2848/7080/files/toiletcomapatibilityquiz_-_under_sink.jpg?v=1777420157' },
        { value: 'none', label: 'No',                                 image: 'https://cdn.shopify.com/s/files/1/0563/2848/7080/files/toiletcomapatibilityquiz_-_no_tank_access.jpg?v=1777420157' }
      ],
      next: () => 'result'
    }
  };

  // Order of questions — used for the `Change` pills to jump back and for
  // rendering the summary above the current question. q4_hose and q4_valve
  // share the same slot since they're on exclusive branches; STEPS lists
  // hose before valve for consistent iteration order.
  const STEPS = ['q1_type', 'q2_screw', 'q3_tank', 'q4_hose', 'q4_valve'];

  const FIELD_LABELS = {
    type:  'Toilet type',
    screw: 'Screw to tank distance',
    tank:  'Access to tank connection',
    hose:  'Hose connection type',
    valve: 'Water valve access',
    model: 'Toilet model'
  };

  const VALUE_LABELS = {
    two_piece: 'Two-Piece',
    one_piece: 'One-Piece',
    other:     'Other',
    more:      'More than 1"',
    less:      'Less than 1"',
    yes:       'Yes',
    no:        'No',
    rigid:     'Rigid',
    flexible:  'Flexible',
    wall:      'Yes, on the wall behind my toilet',
    sink:      'Yes, under a nearby sink',
    none:      'No'
  };

  // ---------- Result resolver ---------------------------------------------
  // Maps an answers object to design_1…design_8. When a model is selected,
  // `compatibility` comes pre-filled from the metaobject:
  //   • not_compatible  → design_7 (known incompatible toilet)
  //   • needs_alt_parts → tank auto-set to 'no' (user skips q3_tank)
  //   • compatible      → runs full quiz (tank question included)
  //
  // Design semantics:
  //   design_1 = compatible with alternate parts (full range — NEO/NEO Plus/Whift)
  //   design_2 = compatible + hose replacement (full range)
  //   design_3 = fully compatible (full range)
  //   design_4 = compatible with alternate parts (NEO Plus + Whift only)
  //   design_5 = compatible + hose replacement (NEO Plus + Whift only)
  //   design_6 = fully compatible (NEO Plus + Whift only)
  //   design_7 = toilet not compatible (catches not_compatible + valve=none)
  //   design_8 = "let's talk" / contact us (type=other — unknown toilet)
  //
  // screw=more ⇒ full product range fits (designs 1, 2, 3).
  // screw=less ⇒ narrower set: NEO Plus + Whift only (designs 4, 5, 6).
  //
  // Within each screw bucket:
  //   tank=yes + hose=flexible    → fully compatible (design 3/6)
  //   tank=yes + hose=rigid       → compatible + flex hose (design 2/5)
  //   tank=no  + valve=wall|sink  → compatible + alt kit (design 1/4)
  //   tank=no  + valve=none       → toilet not compatible (design 7)
  function resolveResult(a) {
    // Short-circuit: metaobject explicitly marked as incompatible.
    if (a.compatibility === 'not_compatible') return 'design_7';
    // Unknown toilet type from the quiz — we can't evaluate. Route to the
    // "let's talk" page so a team member can help figure out compatibility.
    if (a.type === 'other') return 'design_8';
    // No water access anywhere → nothing we can connect.
    if (a.valve === 'none') return 'design_7';

    // Guardrail: needs_alt_parts can't reach tank=yes paths (design 2/3/5/6).
    // Normalise in case state was tampered with via URL params.
    if (a.compatibility === 'needs_alt_parts') {
      a = Object.assign({}, a, { tank: 'no' });
    }

    // screw=more (or two-piece, which implies 'more') ⇒ full product range.
    if (a.screw === 'more') {
      if (a.tank === 'yes') {
        if (a.hose === 'flexible') return 'design_3'; // compatible
        if (a.hose === 'rigid')    return 'design_2'; // + flex hose
      }
      if (a.valve === 'wall' || a.valve === 'sink') return 'design_1'; // + alt kit
    }

    // screw=less ⇒ narrower product set (NEO Plus + Whift).
    if (a.tank === 'yes') {
      if (a.hose === 'flexible') return 'design_6'; // compatible
      if (a.hose === 'rigid')    return 'design_5'; // + flex hose
    }
    if (a.valve === 'wall' || a.valve === 'sink') return 'design_4'; // + alt kit

    return 'design_8'; // fallback — shouldn't normally hit this
  }

  // ---------- Lightweight fuzzy search ------------------------------------
  // Strict word-start matching against the full display name. Every token
  // must match the start of a *distinct* word — so typing "kohler k" needs
  // two different K-words in the display name: one satisfies "kohler", the
  // other satisfies "k". Adair fails because "Kohler Adair" only has one
  // K-word. Kathryn wins because "Kohler Kathryn" has two.
  //
  // Tie-break alphabetically. Cap at 5, no backfill.
  function searchModels(query, models, limit = 5) {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    const tokens = q.split(/\s+/).filter(Boolean);

    const scored = models
      .map(m => {
        const hay = displayName(m).toLowerCase();
        // Collect every word-start position in the haystack. A word-start
        // is index 0 or any char immediately after a non-letter/digit.
        const wordStarts = [];
        if (hay.length) wordStarts.push(0);
        for (let i = 1; i < hay.length; i++) {
          if (!/[a-z0-9]/.test(hay[i - 1]) && /[a-z0-9]/.test(hay[i])) {
            wordStarts.push(i);
          }
        }

        const claimed = new Set();
        let score = 0;
        for (const t of tokens) {
          let matchIdx = -1;
          for (const pos of wordStarts) {
            if (claimed.has(pos)) continue;
            if (hay.startsWith(t, pos)) { matchIdx = pos; break; }
          }
          if (matchIdx === -1) return null;
          claimed.add(matchIdx);
          score += 3;
        }

        return { model: m, score };
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return displayName(a.model).localeCompare(displayName(b.model));
      });

    return scored.slice(0, limit).map(r => r.model);
  }

  function highlight(text, query) {
    const q = query.trim();
    if (!q) return escapeHtml(text);
    const re = new RegExp('(' + q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')', 'ig');
    return escapeHtml(text).replace(re, '<mark>$1</mark>');
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  // Prepends the brand when the model name doesn't already start with it
  // (case-insensitive). Keeps search results readable without doubling up —
  // `Caroma Brisbane 270` stays as-is, but bare `Colby` becomes `Aquasource Colby`.
  function displayName(m) {
    if (!m.brand) return m.name;
    const nameLower = m.name.toLowerCase();
    const brandLower = m.brand.toLowerCase();
    if (nameLower === brandLower || nameLower.startsWith(brandLower + ' ')) {
      return m.name;
    }
    return `${m.brand} ${m.name}`;
  }

  // ---------- Renderers ---------------------------------------------------
  // Renders the stacked list of search-result buttons. Flat list — each
  // result already includes the brand in its label (via displayName), so
  // an extra group header above them would be redundant.
  function renderResultList(query, results) {
    if (!results.length) {
      return '<p class="body body--sm text--subcaption">No matches — try taking the quiz.</p>';
    }
    return `<div>${results.map(m => `
      <button type="button" class="body body--md tf__result" data-tf-action="select-model" data-model-id="${escapeHtml(m.id)}">
        <span>${highlight(displayName(m), query)}</span>
        <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
          <path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M5 12h14m-6-6 6 6-6 6"/>
        </svg>
      </button>
    `).join('')}</div>`;
  }

  function renderSearchResults(query, results, tf) {
    if (!results.length) {
      return `<p class="body body--sm text--subcaption mt-3 mb-3">No matches for &ldquo;${escapeHtml(query)}&rdquo;. Try a broader search or take the quiz.</p>`;
    }

    return `
      <p class="body body--sm text--subcaption mt-3 mb-3">${results.length} result${results.length === 1 ? '' : 's'} for &ldquo;${escapeHtml(query)}&rdquo;</p>
      ${renderResultList(query, results)}
      <div class="cptn cptn--md text--subcaption tf__divider d-flex align-items-center gap-3 mt-4" role="presentation"><span>or</span></div>
      <button type="button" class="btn btn--sm btn--secondary tf__btn-row d-flex align-items-center justify-content-center gap-2 w-100 mt-3" data-tf-action="start-quiz">
        <span>I don&rsquo;t know my toilet. Take the quiz</span>
        <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
          <path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M5 12h14m-6-6 6 6-6 6"/>
        </svg>
      </button>
    `;
  }

  function renderSummary(answers) {
    // A summary row renders for every answered field prior to the current
    // step — but we hide rows for fields the user never actually saw a
    // question for:
    //   • type:  embedded in the model name when a model is selected
    //   • screw: pre-filled to 'more' silently when type is 'two_piece'
    //   • tank:  pre-filled silently when compatibility is known
    //            (needs_alt_parts → 'no'; compatible → 'yes')
    const rows = [];
    if (answers.model) {
      rows.push({ label: FIELD_LABELS.model, value: answers.modelName, step: 'model' });
    }
    for (const step of STEPS) {
      const field = FLOW[step].field;
      if (answers[field] == null) continue;
      if (step === 'q1_type' && answers.model) continue;
      if (step === 'q2_screw' && answers.type === 'two_piece') continue;
      if (step === 'q3_tank' && answers.compatibility === 'needs_alt_parts') continue;
      rows.push({
        label: FIELD_LABELS[field],
        value: VALUE_LABELS[answers[field]] || answers[field],
        step
      });
    }
    return rows.map(r => `
      <div class="tf__summary bg--offwhite mb-3">
        <div class="tf__summary-body">
          <span class="cptn cptn--sm text--subcaption d-flex">${escapeHtml(r.label)}</span>
          <span class="body-title body-title--md d-flex mt-1">${escapeHtml(r.value)}</span>
        </div>
        <button type="button" class="body body--md text--link text--underline" style="border:0;background:transparent;cursor:pointer;padding:0.4rem 0.8rem;" data-tf-action="change" data-step="${r.step}">Change</button>
      </div>
    `).join('');
  }

  // Walk the FLOW from q1_type to 'result' to determine which questions
  // the user will visibly see on their current path. Used to render the
  // progress bar with the correct number of segments — paths vary in
  // length (e.g. type=other is 1 question, two-piece + tank=yes is 3,
  // one-piece + tank=no is 4) so the bar must match the actual path.
  //
  // For unanswered branching points we pick a default that maximises the
  // visible step count, so the bar doesn't surprise users by growing when
  // they answer (only shrinking is acceptable, since a shorter path is a
  // genuine outcome of their choice).
  //
  // "Visible" means the user actually answers it. Auto-filled steps
  // (screw='more' for two_piece, tank='no' for needs_alt_parts) are
  // skipped because the user never sees a question for them.
  function computePath(answers) {
    const auto = Array.isArray(answers.__autoFilled) ? answers.__autoFilled : [];
    const path = [];
    // Selecting a model from search counts as a step — prepend it so the
    // bar shows it as a completed segment ahead of the questions. Skipped
    // for the quiz-only flow where no model is selected.
    if (answers.model) path.push('model');

    let stepId = 'q1_type';
    const guard = STEPS.length + 2; // safety: prevent infinite loops
    let i = 0;
    while (stepId && stepId !== 'result' && FLOW[stepId] && i++ < guard) {
      const step = FLOW[stepId];
      const field = step.field;
      // Skip steps whose field was filled silently (auto-fill from the
      // model's metaobject type or from cascade rules in applyAutoFills).
      if (!auto.includes(field)) path.push(stepId);

      // Pick the next step. Use the real answer if known; otherwise a
      // default that produces the longest path for unanswered branches —
      // so the bar doesn't surprise users by growing when they answer.
      let val = answers[field];
      if (val == null) {
        if (field === 'type') val = 'one_piece';      // longest branch (vs two_piece skips q2_screw, vs other ends immediately)
        else if (field === 'tank') val = 'no';        // q4_valve and q4_hose have same length, pick arbitrarily
        else val = step.options[0].value;             // fallback: first option
      }
      stepId = typeof step.next === 'function' ? step.next(val, answers) : step.next;
    }
    return path;
  }

  function renderProgress(answers, currentStep) {
    // Hide the bar on the very first question of the quiz-only flow.
    // Reason: on q1_type with no model, every segment we'd render is
    // unfilled, so the bar adds visual noise without conveying progress.
    // It also lets us start the bar fresh once the user makes their
    // first choice — at that point the path becomes determinate (e.g.
    // two_piece skips q2_screw) and the segment count is meaningful.
    // Doesn't affect model-selected flows: those have a leading "model"
    // segment that's already filled, so the bar is informative immediately.
    if (currentStep === 'q1_type' && !answers.model) return '';

    const path = computePath(answers);
    if (path.length === 0) return '';
    const currentIdx = path.indexOf(currentStep);
    // If currentStep isn't in the visible path (e.g. user is mid-quiz on
    // an auto-filled step that somehow rendered), bail rather than show
    // garbage. Should not normally happen.
    if (currentIdx < 0) return '';
    return `
      <div class="tf__progress" role="progressbar" aria-label="Quiz progress" aria-valuemin="0" aria-valuemax="${path.length}" aria-valuenow="${currentIdx}">
        ${path.map((_, i) => `
          <div class="tf__progress-segment${i < currentIdx ? ' tf__progress-segment--done' : ''}"></div>
        `).join('')}
      </div>
    `;
  }

  function renderQuestion(stepId, answers) {
    const step = FLOW[stepId];
    const summary = renderSummary(filterAnswersUpTo(answers, stepId));
    const progress = renderProgress(answers, stepId);

    // Choices: card-style for cards-kind questions (toilet type, valve),
    // outline-pill style for binary questions (more/less, yes/no, rigid/flex).
    // Each card option pulls its own image from the FLOW definition
    // (o.image, set in the q1_type / q4_valve options at the top of this
    // file). Edit those URLs there in one place rather than search-and-
    // replace through the renderer.
    const choicesHtml = step.kind === 'cards'
      ? `<div class="tf__choices tf__choices--${step.options.length}">${step.options.map(o => `
          <button type="button" class="tf__choice" data-tf-action="answer" data-value="${escapeHtml(o.value)}">
            <img class="tf__choice-media" src="${escapeHtml(o.image || '#')}" alt="">
            <span class="body body--lg tf__choice-label">${escapeHtml(o.label)}</span>
          </button>
        `).join('')}</div>`
      : `<div class="tf__binary">${step.options.map(o => `
          <button type="button" class="btn btn--secondary" data-tf-action="answer" data-value="${escapeHtml(o.value)}">
            ${escapeHtml(o.label)}
          </button>
        `).join('')}</div>`;

    // The whole question (title + hint image + choices) sits inside a
    // dark-offwhite rounded card, per the redesigned Figma layout. The
    // progress bar and summary pills sit outside this card, above it.
    return `
      ${progress}
      ${summary}
      <div class="tf__question bg--offwhite">
        <h3 class="heading heading--sm tf__question-title">${escapeHtml(step.question)}</h3>
        ${step.hintImage ? `<img class="tf__question-hint" src="${escapeHtml(step.hintImage)}" alt="">` : ''}
        ${choicesHtml}
      </div>
    `;
  }

  function filterAnswersUpTo(answers, stepId) {
    // Summary shows answers from steps *before* the current one. Carry
    // model / modelName / compatibility through so renderSummary can apply
    // its hide rules (tank suppression for needs_alt_parts models, type
    // merge into model name, etc.).
    const cutoff = STEPS.indexOf(stepId);
    const kept = {
      model: answers.model,
      modelName: answers.modelName,
      compatibility: answers.compatibility
    };
    for (let i = 0; i < cutoff; i++) {
      const f = FLOW[STEPS[i]].field;
      if (answers[f] != null) kept[f] = answers[f];
    }
    return kept;
  }

  // Once a result is resolved, the widget itself shows only the summary
  // pills (so the user can still Change any answer). The matching
  // design_N section lives elsewhere on the page and is toggled by
  // showResultSection() below.
  function renderResult(answers /*, design */) {
    return renderSummary(answers);
  }

  // Hide every result section, then reveal the one matching `design`.
  // Each section is a DOM element whose ID is 'design_1' through
  // 'design_8'. We toggle the `.tf-is-visible` class — the CSS rule in
  // the snippet handles the actual display switch with !important to
  // beat PageFly's own styles.
  function showResultSection(design) {
    let target = null;
    for (let i = 1; i <= 8; i++) {
      const el = document.getElementById('design_' + i);
      if (!el) continue;
      if (('design_' + i) === design) {
        el.classList.add('tf-is-visible');
        target = el;
      } else {
        el.classList.remove('tf-is-visible');
      }
    }
    // Scroll the revealed section into view. Deferred one frame so the
    // browser has a chance to apply the display change before we measure.
    // scrollMarginTop offsets the scroll landing point so the section sits
    // below the sticky header rather than vanishing under it:
    //   ≥1026px → 139px (desktop header height)
    //   <1026px → 88px  (mobile/tablet header height)
    // Applied inline so it beats any PageFly section styles.
    if (target) {
      const offset = window.matchMedia('(min-width: 1026px)').matches ? 139 : 88;
      target.style.scrollMarginTop = `${offset}px`;
      requestAnimationFrame(() => {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }

  function hideAllResultSections() {
    for (let i = 1; i <= 8; i++) {
      const el = document.getElementById('design_' + i);
      if (el) el.classList.remove('tf-is-visible');
    }
  }

  // ---------- Controller --------------------------------------------------
  function init(root) {
    window.__tfRoot = root;

    const models = JSON.parse(root.querySelector('[data-tf-models]').textContent || '[]');
    const landing = root.querySelector('[data-tf-view="landing"]');
    const dynamic = root.querySelector('[data-tf-view="dynamic"]');
    const searchInput = root.querySelector('[data-tf-search]');

    let answers = {}; // { type, screw, tank, valve, model?, modelName? }
    let currentStep = null;

    // ----- View switching -----
    function showLanding() {
      landing.hidden = false;
      dynamic.hidden = true;
      dynamic.innerHTML = '';
      currentStep = null;
      // Clear any leftover search panel state when returning to landing.
      const panel = landing.querySelector('[data-tf-search-panel]');
      if (panel) { panel.hidden = true; panel.innerHTML = ''; }
      if (searchInput) searchInput.value = '';
      // Returning to landing always wipes any visible result section.
      hideAllResultSections();
      setUrl({});
    }

    function showDynamic(html) {
      landing.hidden = true;
      dynamic.hidden = false;
      dynamic.innerHTML = html;
      // Move focus to first interactive element for a11y
      const first = dynamic.querySelector('button, input, a');
      if (first) first.focus({ preventScroll: false });
    }

    // ----- Search -----
    function handleSearch() {
      const q = searchInput.value;
      const panel = landing.querySelector('[data-tf-search-panel]');
      if (q.trim().length < 2) {
        if (panel) { panel.hidden = true; panel.innerHTML = ''; }
        return;
      }
      const results = searchModels(q, models);
      panel.hidden = false;
      panel.innerHTML = `
        <p class="body body--sm text--subcaption mt-3 mb-3">${results.length} result${results.length === 1 ? '' : 's'} for &ldquo;${escapeHtml(q)}&rdquo;</p>
        ${renderResultList(q, results)}
      `;
    }

    // ----- Quiz flow -----
    function goto(stepId) {
      // Skip any step whose field already has an answer — handles cases like
      // needs_alt_parts models where tank is pre-filled to 'no' in
      // selectModel(). Without this, FLOW.q2_screw.next always hardcodes
      // 'q3_tank' and the user would get asked the tank question anyway.
      while (stepId && stepId !== 'result' && FLOW[stepId] && answers[FLOW[stepId].field] != null) {
        const step = FLOW[stepId];
        const val = answers[step.field];
        const next = typeof step.next === 'function' ? step.next(val, answers) : step.next;
        stepId = next;
      }

      if (stepId === 'result') {
        const design = resolveResult(answers);
        // Fire once-per-session: a fresh quiz (selectModel/startQuiz)
        // resets the flag, but Change-driven re-resolves to a different
        // design within the same quiz won't refire the event count.
        // The design tag is set every time so the most recent result
        // shows up in Clarity even when the event count stays at 1.
        trackEndResults(design);
        showDynamic(renderResult(answers, design));
        showResultSection(design);
        setUrl({ step: 'result', design });
        return;
      }
      // Any step other than 'result' means the user is mid-quiz — ensure any
      // previously-shown result section gets hidden.
      hideAllResultSections();
      currentStep = stepId;
      showDynamic(renderQuestion(stepId, answers));
      setUrl({ step: stepId, ...answers });
    }

    function answer(value) {
      if (!currentStep) return;
      const step = FLOW[currentStep];
      answers[step.field] = value;

      // When the user answers toilet type and a model is already selected,
      // append the type to the visible model name so the summary pill reads
      // "American Standard Saver - Two-Piece" instead of just "Saver".
      if (step.field === 'type' && answers.model) {
        const typeLabel = VALUE_LABELS[value] || value;
        // Strip any previous type suffix before appending (in case user changed type).
        const baseName = (answers.modelName || '').replace(/\s-\s(One-Piece|Two-Piece|Other)$/, '');
        answers.modelName = value === 'other' ? baseName : `${baseName} - ${typeLabel}`;
      }

      // When the user switches away from two_piece, a previously auto-filled
      // screw='more' is no longer valid — clear it so they see q2_screw.
      if (step.field === 'type' && value !== 'two_piece' && answers.screw === 'more') {
        // Only clear if it was auto-set (no way to tell for sure, but
        // 'more' is the two-piece default, so this is safe in practice).
        delete answers.screw;
      }

      // Apply any auto-fills that this new answer triggers (e.g. type=two_piece
      // → screw='more'). Centralised so the same rules run in change() too.
      applyAutoFills();

      persist();
      const next = typeof step.next === 'function' ? step.next(value, answers) : step.next;
      goto(next);
    }

    // Re-apply every auto-fill whose *trigger* field is still present in
    // the answers object. An auto-fill is a field we set silently based on
    // another answer (or metaobject data), so the user never sees a
    // question for it. When Change clears a range of answers, it might
    // wipe an auto-fill whose trigger is still valid — this helper
    // restores it so the subsequent goto() correctly skips the question.
    //
    // Current auto-fills:
    //   model with type=one_piece|two_piece ⇒ answers.type = that value
    //   type='two_piece'                    ⇒ screw='more'
    //   compatibility='needs_alt_parts'     ⇒ tank='no'
    //
    // Note: `compatible` models do NOT auto-fill tank — we ask the tank
    // question explicitly so the full quiz applies (per the flow chart).
    //
    // Order matters here: the model→type fill must run before the
    // type→screw fill so that the 'more' cascade sees the fresh type.
    function applyAutoFills() {
      // Track which fields we auto-fill so computePath() can distinguish
      // "user answered" from "we set this silently". Array (not Set) so
      // sessionStorage round-trips cleanly via JSON.stringify.
      if (!Array.isArray(answers.__autoFilled)) answers.__autoFilled = [];
      const markAuto = (field) => {
        if (!answers.__autoFilled.includes(field)) answers.__autoFilled.push(field);
      };

      // Model type pre-fill: look up the selected model and, if it has a
      // known one/two-piece classification, skip q1_type entirely. Models
      // whose type is 'other' or unset leave answers.type alone so the
      // user is asked the question.
      if (answers.model && answers.type == null) {
        const m = models.find(x => x.id === answers.model);
        if (m && (m.type === 'one_piece' || m.type === 'two_piece')) {
          answers.type = m.type;
          markAuto('type');
        }
      }
      // Two-piece ⇒ >1" screw-to-tank clearance implicitly. Works whether
      // the type came from the model (auto-fill above) or the user picked
      // it via q1_type.
      if (answers.type === 'two_piece' && answers.screw == null) {
        answers.screw = 'more';
        markAuto('screw');
      }
      if (answers.compatibility === 'needs_alt_parts' && answers.tank == null) {
        answers.tank = 'no';
        markAuto('tank');
      }
    }

    function change(stepId) {
      // Top-of-stack Change buttons bounce back to the landing screen:
      //   • model (for model-selected flow)
      //   • q1_type when no model is selected (for quiz-only flow)
      // Either way, this represents "start over" — wipe everything.
      const isTopOfStack = stepId === 'model' || (stepId === 'q1_type' && !answers.model);
      if (isTopOfStack) {
        answers = {};
        persist();
        showLanding();
        searchInput.focus();
        return;
      }
      // Clear this step's answer + anything downstream.
      const idx = STEPS.indexOf(stepId);
      const cleared = new Set();
      for (let i = idx; i < STEPS.length; i++) {
        const field = FLOW[STEPS[i]].field;
        delete answers[field];
        cleared.add(field);
      }
      // Drop stale auto-fill flags for cleared fields. applyAutoFills will
      // re-set them as appropriate.
      if (Array.isArray(answers.__autoFilled)) {
        answers.__autoFilled = answers.__autoFilled.filter(f => !cleared.has(f));
      }
      // If clearing type and a model is selected, strip the type suffix from
      // the model name so the summary doesn't show a stale " - Two-Piece".
      if (stepId === 'q1_type' && answers.model && answers.modelName) {
        answers.modelName = answers.modelName.replace(/\s-\s(One-Piece|Two-Piece|Other)$/, '');
      }
      // Reapply any auto-fills whose triggers are still valid so the user
      // doesn't re-encounter questions they previously skipped. E.g. for a
      // model with compatibility='compatible', tank='yes' was auto-set; the
      // loop above deleted it, so we put it back here.
      applyAutoFills();
      persist();
      goto(stepId);
    }

    function selectModel(id) {
      const m = models.find(x => x.id === id);
      if (!m) return;
      // Fire the start event before any short-circuits so even
      // not_compatible models that skip the quiz still register as
      // having begun it (the user did engage). Uses the model's display
      // name (brand-prefixed when the name doesn't already lead with the
      // brand) so the Clarity tag is human-readable.
      trackStartSearch(displayName(m));
      answers.model = m.id;
      answers.modelName = displayName(m);
      answers.compatibility = m.compatibility || null;

      // If the model has a known type, append " - One-Piece" / " - Two-Piece"
      // to the display name so the summary pill reads consistently with the
      // user-answered case (where the suffix gets appended in answer()).
      if (m.type === 'one_piece' || m.type === 'two_piece') {
        const typeLabel = m.type === 'one_piece' ? 'One-Piece' : 'Two-Piece';
        answers.modelName = `${answers.modelName} - ${typeLabel}`;
      }

      // Short-circuit: known-incompatible toilets never run the quiz.
      if (answers.compatibility === 'not_compatible') {
        persist();
        goto('result');
        return;
      }

      // applyAutoFills sets derived answers from model data and cascades:
      //   model.type=one_piece|two_piece   ⇒ answers.type
      //   answers.type=two_piece           ⇒ screw='more'
      //   compatibility='needs_alt_parts'  ⇒ tank='no'
      applyAutoFills();
      persist();
      // Enter at q1_type — goto's loop will skip any already-answered steps
      // (respecting branching). For a model with type='one_piece', the loop
      // sees answers.type is set and skips q1_type; for type='other' models,
      // q1_type is the first question the user sees.
      goto('q1_type');
    }

    function startQuiz() {
      trackStartIDK();
      answers = {};
      persist();
      goto('q1_type');
    }

    // ----- Persistence -----
    function persist() {
      try { sessionStorage.setItem('tf:answers', JSON.stringify(answers)); } catch (_) {}
    }

    function restore() {
      try {
        const saved = JSON.parse(sessionStorage.getItem('tf:answers') || '{}');
        if (saved && typeof saved === 'object') answers = saved;
      } catch (_) {}

      const url = new URL(window.location.href);
      const step = url.searchParams.get('tf_step');
      if (step === 'result') {
        const design = url.searchParams.get('tf_design') || resolveResult(answers);
        showDynamic(renderResult(answers, design));
      } else if (step && FLOW[step]) {
        currentStep = step;
        showDynamic(renderQuestion(step, answers));
      }
    }

    function setUrl(params) {
      const url = new URL(window.location.href);
      // Clear prior tf_* params then re-apply
      [...url.searchParams.keys()].filter(k => k.startsWith('tf_')).forEach(k => url.searchParams.delete(k));
      if (params.step) url.searchParams.set('tf_step', params.step);
      if (params.design) url.searchParams.set('tf_design', params.design);
      history.replaceState({}, '', url.toString());
    }

    // ----- Event wiring -----
    let searchTimer;
    searchInput.addEventListener('input', () => {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(handleSearch, 120);
    });

    root.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-tf-action]');
      if (!btn) return;
      const action = btn.dataset.tfAction;
      if (action === 'start-quiz') startQuiz();
      else if (action === 'answer') answer(btn.dataset.value);
      else if (action === 'change') change(btn.dataset.step);
      else if (action === 'select-model') selectModel(btn.dataset.modelId);
    });

    restore();
  }

  // Initialise all instances on the page (handles Shopify section reloads
  // in the theme editor too).
  function initAll() {
    document.querySelectorAll('[data-tf-root]').forEach(el => {
      if (el.__tfInit) return;
      el.__tfInit = true;
      init(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify section reload hooks (theme editor)
  document.addEventListener('shopify:section:load', initAll);
})();